<script setup>
import { ref } from "vue";

const props = defineProps({
  campaign: {
    type: Object,
    required: true
  },
  update: {
    type: Function,
    required: true
  }
});

const selectedType = ref(props.campaign.layout_type || "homepage");

const updateType = (type) => {
  selectedType.value = type;
  props.update("layout_type", type);
};

const layoutTypes = {
  homepage: {
    title: "홈페이지 타입",
    description: "프로필 정보가 포함된 상세한 홈페이지 레이아웃",
    details: "캠페인 배경, 프로필 사진, 캠페인 타이틀, 메시지 설정 가능",
    icon: "home"
  },
  landing: {
    title: "랜딩페이지 타입",
    description: "간결한 콘텐츠 중심의 랜딩페이지 레이아웃",
    details: "핀들 페이지들 중 '기본'으로 설정된 페이지가 메인으로 표시",
    icon: "layout"
  }
};
</script>

<template>
  <div>
    <div class="mb-6">
      <h2 class="mb-3 text-sm font-medium uppercase tracking-wide text-500">레이아웃 선택</h2>

      <!-- Modern Card Selection UI -->
      <div class="grid grid-cols-2 gap-4">
        <div
          v-for="(type, key) in ['homepage', 'landing']"
          :key="key"
          class="relative cursor-pointer overflow-hidden rounded-xl border transition-all duration-300"
          :class="[
            selectedType === type
              ? 'border-blue-500 bg-blue-50/50 ring-2 ring-blue-200 dark:border-blue-400 dark:bg-blue-900/20 dark:ring-blue-800'
              : 'bg-white border-200 hover:border-300 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-600'
          ]"
          @click="updateType(type)"
        >
          <!-- Selection indicator -->
          <div
            v-if="selectedType === type"
            class="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-blue-500 dark:bg-blue-600"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 text-white" viewBox="0 0 20 20" fill="currentColor">
              <path
                fill-rule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clip-rule="evenodd"
              />
            </svg>
          </div>

          <!-- Icon -->
          <div class="p-4 pb-2">
            <div class="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/50">
              <svg
                v-if="type === 'homepage'"
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5 text-blue-600 dark:text-blue-400"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                />
              </svg>
              <svg
                v-else
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5 text-blue-600 dark:text-blue-400"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"
                />
              </svg>
            </div>

            <h3 class="font-medium text-900">{{ layoutTypes[type].title }}</h3>
            <p class="mt-1 line-clamp-2 text-xs text-500">{{ layoutTypes[type].description }}</p>
          </div>

          <!-- Bottom info bar -->
          <div class="border-t px-4 py-2 text-xs text-500 bg-50 border-100 dark:border-gray-700 dark:bg-gray-800/50">
            {{ layoutTypes[type].details }}
          </div>
        </div>
      </div>

      <!-- Preview Section -->
      <div class="mt-6 overflow-hidden rounded-xl border bg-white border-200 dark:border-gray-700 dark:bg-gray-800">
        <div class="border-b px-4 py-3 bg-50 border-200 dark:border-gray-700 dark:bg-gray-800/80">
          <h3 class="text-sm font-medium text-700">미리보기</h3>
        </div>

        <div class="p-4">
          <div class="flex aspect-video items-center justify-center rounded-lg bg-100 dark:bg-gray-900/50">
            <div v-if="selectedType === 'homepage'" class="w-full max-w-xs">
              <!-- Homepage preview mockup -->
              <div class="relative h-16 overflow-hidden rounded-t-lg bg-blue-500 dark:bg-blue-700">
                <div
                  class="absolute bottom-0 left-1/2 h-16 w-16 -translate-x-1/2 translate-y-1/3 transform rounded-full border-4 border-white bg-200"
                ></div>
              </div>
              <div class="rounded-b-lg bg-white p-4 pt-8 shadow-sm dark:bg-gray-800">
                <div class="mx-auto mb-2 h-4 w-3/4 rounded bg-200"></div>
                <div class="mx-auto mb-4 h-3 w-1/2 rounded bg-100"></div>
                <div class="flex justify-center gap-2">
                  <div class="h-8 w-8 rounded-full bg-100"></div>
                  <div class="h-8 w-8 rounded-full bg-100"></div>
                </div>
              </div>
            </div>

            <div v-else class="w-full max-w-xs">
              <!-- Landing page preview mockup -->
              <div class="overflow-hidden rounded-lg bg-white shadow-sm dark:bg-gray-800">
                <div class="flex h-24 items-center justify-center bg-100">
                  <div class="h-6 w-16 rounded bg-200"></div>
                </div>
                <div class="p-4">
                  <div class="mb-3 h-4 w-3/4 rounded bg-200"></div>
                  <div class="mb-1 h-3 w-full rounded bg-100"></div>
                  <div class="mb-1 h-3 w-full rounded bg-100"></div>
                  <div class="mb-3 h-3 w-2/3 rounded bg-100"></div>
                  <div class="mx-auto h-6 w-1/3 rounded-full bg-blue-100 dark:bg-blue-900/50"></div>
                </div>
              </div>
            </div>
          </div>

          <p class="mt-3 text-center text-sm text-500">
            {{ selectedType === "homepage" ? "프로필 정보가 포함된 상세한 홈페이지 레이아웃" : "간결한 콘텐츠 중심의 랜딩페이지 레이아웃" }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
